import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import numeral from 'numeral'
import {
  AutoPaySettings,
  LeaseApplicationStatus,
  PaymentAccount,
  PaymentType
} from '../types/app-types'
import PaymentMethodsResponse from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/PaymentMethodsResponse'

export function hasBalance(value: number): boolean {
  return Math.abs(value) > 0
}

export function formatBillingPeriod(s: string): string {
  try {
    return format(parseISO(s), 'MMMM yyyy')
  } catch (e) {
    console.warn('Failed to format billing period', s)
    return ''
  }
}

export function formatDate(d: Date): string {
  try {
    return format(d, 'MMM d, yyyy')
  } catch (e) {
    console.warn('Failed to format date', d)
    return ''
  }
}

export function formatDateMonth(d: Date): string {
  try {
    return format(d, 'MMM')
  } catch (e) {
    console.warn('Failed to format date', d)
    return ''
  }
}

export function formatDateMonthDay(d: Date): string {
  try {
    return format(d, 'MMM d')
  } catch (e) {
    console.warn('Failed to format date', d)
    return ''
  }
}

export function formatDateMonthString(s: string): string {
  try {
    return format(parseISO(s), 'MMM')
  } catch (e) {
    console.warn('Failed to format date', s)
    return ''
  }
}

export function formatDateMonthDayString(s: string): string {
  try {
    return format(parseISO(s), 'MMM d')
  } catch (e) {
    console.warn('Failed to format date', s)
    return ''
  }
}

export function formatDateString(s: string): string {
  try {
    return formatDate(parseISO(s))
  } catch (e) {
    console.warn('Failed to format date string', s)
    return ''
  }
}

export function formatDayOfMonth(n: number): string {
  if (n > 3 && n < 21) {
    return `${n}th`
  }
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

export function formatAmount(n: number | string): string {
  return numeral(n).format('$0,0.00')
}

export function formatDecimal(n: number | string): string {
  return numeral(n).format('0.00')
}

export function formatPaymentAccount({
  adyenPaymentMethod,
  adyenAccountLast4,
  adyenExpires
}: PaymentAccount): string {
  switch (adyenPaymentMethod) {
    case 'Direct Debit':
      return `x${adyenAccountLast4}`
    case 'Credit Card':
      return `x${adyenAccountLast4} - ${adyenExpires}`
    default:
      return `x${adyenAccountLast4}`
  }
}

export function formatPaymentAccountFull(
  paymentAccount: PaymentAccount
): string {
  return `${paymentAccount.adyenPaymentIssuer} ${
    paymentAccount.adyenPaymentMethod
  } - ${formatPaymentAccount(paymentAccount)}`
}

export function findAutoPaySettings(
  paymentAccount: PaymentAccount,
  accountId: string
): AutoPaySettings {
  if (paymentAccount && paymentAccount.reservationAutoPay) {
    return paymentAccount.reservationAutoPay[accountId]
  }
  return
}

export function findAutoPayAccount(
  paymentAccounts: PaymentAccount[],
  accountId: string
): PaymentAccount {
  if (paymentAccounts && paymentAccounts.length) {
    for (const paymentAccount of paymentAccounts) {
      if (
        paymentAccount.reservationAutoPay &&
        paymentAccount.reservationAutoPay[accountId]
      ) {
        return paymentAccount
      }
    }
  }
  return
}

export function sortBy<T extends Record<string, any>>(
  items: T[],
  sortProp: string
): T[] {
  const comparator = (x1: T, x2: T): number => {
    const sortKey1: any = x1[sortProp]
    const sortKey2: any = x2[sortProp]
    if (sortKey1 < sortKey2) {
      return 1
    } else if (sortKey1 > sortKey2) {
      return -1
    } else {
      return 0
    }
  }
  return [...items].sort(comparator)
}

export interface PropertyLegacyBilling {
  portalUrl: string
  transitionDate: Date
}

/**
 * Get the legacy portal URL for the property
 * @param propertyId
 */
export function getLegacyBillingInfo(
  propertyId: string
): PropertyLegacyBilling {
  const portalUrls: any = {
    'CADNASH': {
      portalUrl:
        'https://cadence-nashville.securecafe.com/residentservices/the-cadence0/userlogin.aspx',
      transitionDate: new Date('2022/08/01')
    },
    'WHYDCPENN': {
      portalUrl:
        'https://cadence-nashville.securecafe.com/residentservices/the-cadence0/userlogin.aspx',
      transitionDate: new Date('2022/08/01')
    },
    'TEST': {
      portalUrl:
        'https://cadence-nashville.securecafe.com/residentservices/the-cadence0/userlogin.aspx',
      transitionDate: new Date('2022/08/01')
    },
    'FAWNYC': {
      portalUrl:
        'https://frontandwall.securecafe.com/residentservices/front-wall-street-operated-by-whyhotel/userlogin.aspx',
      transitionDate: new Date('2022/11/01')
    }
  }

  return portalUrls[propertyId] as PropertyLegacyBilling
}

export function hasApplicationStatus(
  currentStatus: LeaseApplicationStatus,
  targetStatus: LeaseApplicationStatus
): boolean {
  const statusOrder = [
    LeaseApplicationStatus.FormInProgress,
    LeaseApplicationStatus.FormCompleted,
    LeaseApplicationStatus.PaymentInProgress,
    LeaseApplicationStatus.PaymentCompleted,
    LeaseApplicationStatus.BackgroundCheckInProgress,
    LeaseApplicationStatus.BackgroundCheckCompleted,
    LeaseApplicationStatus.LeaseRejected,
    LeaseApplicationStatus.LeaseApproved
  ]

  const currentIndex = statusOrder.indexOf(currentStatus)
  const targetIndex = statusOrder.indexOf(targetStatus)
  return currentIndex >= 0 ? currentIndex >= targetIndex : false
}

export function filterAllowedPaymentMethods(
  paymentType: PaymentType,
  {paymentMethods}: PaymentMethodsResponse
): PaymentMethodsResponse {
  switch (paymentType) {
    case PaymentType.OneTimePaymentCC:
      return {
        paymentMethods: paymentMethods.filter(({type}) => type !== 'ach')
      } as PaymentMethodsResponse
    case PaymentType.OneTimePaymentACH:
      return {
        paymentMethods: paymentMethods.filter(({type}) => type === 'ach')
      } as PaymentMethodsResponse
    default:
      return {paymentMethods: []} as PaymentMethodsResponse
  }
}

export function parseJwtPayload(token: string): Record<string, any> {
  // break jwt into segments
  const segments = token.split('.')
  if (segments.length !== 3) {
    throw Error('Malformed JWT could not be parsed')
  }

  // decode base64
  const payloadJson = atob(segments[1])

  // parse json
  return JSON.parse(payloadJson)
}

export function isNumeric(value: any): boolean {
  if (typeof value === 'number') {
    return true
  }

  if (typeof value === 'string') {
    // is integer or float
    return /^-?\d+$/.test(value) || /^\d+\.\d+$/.test(value)
  }

  return false
}

export function parseNumber(value: any): number {
  if (typeof value === 'number') {
    return value
  } else if (isNumeric(value)) {
    try {
      return parseFloat(value)
    } catch (e) {
      return 0
    }
  } else {
    return 0
  }
}

export function debounce(callback: Function, wait: number) {
  let timeoutId: any = null
  return (...args: any[]) => {
    window.clearTimeout(timeoutId)
    timeoutId = window.setTimeout(() => {
      callback.apply(null, args)
    }, wait)
  }
}
